import { useIntl } from 'react-intl'
import SolidBuildingIcon from 'LuxKit/Icons/solid/SolidBuildingIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { TimelineEndNode, TimelineNode } from 'components/Common/CancellationPolicies/Flash/CancellationPolicyTimelineComponents'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import type { RestrictedCancellationPolicyView } from 'hooks/useRestrictedCancellationView'

interface Props {
  view: RestrictedCancellationPolicyView
}

function RestrictedCancellationPolicyTimeline({ view }: Props) {
  const intl = useIntl()
  const fullRefundColour = useRebrandSuccessDarkColour()

  return (
    <Group direction="vertical">
      <TimelineNode label={view.startDate}>
        <BodyText variant="medium" weight="bold" colour={fullRefundColour}>
          {intl.formatMessage({
            id: 'xCagtMXm_fccPuCr_nhD',
            defaultMessage: 'Full refund',
            description: 'Section heading for the full refund period in the restricted cancellation timeline',
          })}
        </BodyText>
        <BodyText variant="medium" as="p">
          {intl.formatMessage({
            id: 'mi5nZoqAbs9XqsV4wEP5',
            defaultMessage: 'Cancel before {date} to get a full refund to your original payment method.',
            description: 'Body for the full refund period in the restricted cancellation timeline',
          }, { date: view.refundToOriginalDeadline })}
        </BodyText>
      </TimelineNode>

      <TimelineNode label={view.refundToOriginalDeadline}>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'wVuu8UD7HkRdQN5sB94a',
            defaultMessage: 'Full refund to credit',
            description: 'Section heading for the credit only period in the restricted cancellation timeline',
          })}
        </BodyText>
        <BodyText variant="medium" as="p">
          {intl.formatMessage({
            id: 'GSxIsARYxE6Iuaf-Iv06',
            defaultMessage: 'Cancel before {date} to receive a 100% refund to credit.',
            description: 'Body for the credit only period in the restricted cancellation timeline',
          }, { date: view.creditDeadline })}
        </BodyText>
      </TimelineNode>

      <TimelineNode label={view.creditDeadline}>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'S9D-y7MWUiD8T_3LXOHy',
            defaultMessage: 'No refund',
            description: 'Section heading for the non-refundable period in the restricted cancellation timeline',
          })}
        </BodyText>
        <BodyText variant="medium" as="p">
          {intl.formatMessage({
            id: 'Efwt0J2KlsXw9mPj_Y5t',
            defaultMessage: 'Cancellations after {date} are non-refundable.',
            description: 'Body for the non-refundable period in the restricted cancellation timeline',
          }, { date: view.creditDeadline })}
        </BodyText>
      </TimelineNode>

      <TimelineEndNode
        icon={SolidBuildingIcon}
        label={intl.formatMessage({
          id: 'kG9uFExV5clSkw68hYYi',
          defaultMessage: 'Check-in',
          description: 'Timeline end label marking the check-in date in the restricted cancellation timeline',
        })}
      />
    </Group>
  )
}

export default RestrictedCancellationPolicyTimeline
